import { createFileRoute, Link } from "@tanstack/react-router";
import * as m from "~/paraglide/messages";
import {
  organizationSchema,
  webPageSchema,
  breadcrumbSchema,
  personSchema,
  schemaGraph,
} from "~/lib/seo/schema";
import { JsonLd } from "~/components/seo/JsonLd";

export const Route = createFileRoute("/about")({
  head: () => ({
    meta: [
      { title: m.about_meta_title() },
      { name: "description", content: m.about_meta_description() },
      { property: "og:title", content: m.about_meta_title() },
      { property: "og:description", content: m.about_meta_description() },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://alchema.eu/about" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: m.about_meta_title() },
      { name: "twitter:description", content: m.about_meta_description() },
    ],
  }),
  component: AboutPage,
});

function AboutPage() {
  const jsonLd = schemaGraph(
    organizationSchema(),
    webPageSchema({
      name: m.about_meta_title(),
      description: m.about_meta_description(),
      url: "/about",
    }),
    personSchema({
      id: "vaisakhan",
      name: "Vaisakhan Arackal Sanu",
      jobTitle: "Co-Founder & Engineering",
      description:
        "Full-stack engineer specializing in AI systems and EU data infrastructure.",
      sameAs: ["https://www.linkedin.com/in/vaisakhan-arackal-sanu/"],
      knowsAbout: [
        "AI Engineering",
        "Resume Optimization",
        "GDPR Compliance",
        "Mistral AI",
      ],
    }),
    personSchema({
      id: "chris",
      name: "Chris Zanfir",
      jobTitle: "Co-Founder & Operations",
      description:
        "Operations lead specializing in growth strategy and German regulatory compliance.",
      sameAs: ["https://www.linkedin.com/in/chris-zanfir/"],
      knowsAbout: [
        "Business Development",
        "GDPR",
        "BFSG Accessibility",
        "Startup Operations",
      ],
    }),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: m.about_title(), url: "/about" },
    ]),
  );

  const facts = [
    {
      icon: (
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      label: m.about_facts_founded(),
    },
    {
      icon: (
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      label: m.about_facts_location(),
    },
    {
      icon: (
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
      label: m.about_facts_gdpr(),
    },
    {
      icon: (
        <svg
          className="h-5 w-5"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M12 2a4 4 0 014 4v1a1 1 0 001 1h1a4 4 0 010 8h-1a1 1 0 00-1 1v1a4 4 0 01-8 0v-1a1 1 0 00-1-1H6a4 4 0 010-8h1a1 1 0 001-1V6a4 4 0 014-4z" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
      label: m.about_facts_mistral(),
    },
  ];

  const numbers = [
    { value: "10", label: m.about_numbers_languages() },
    { value: "19", label: m.about_numbers_agents() },
    { value: "12", label: m.about_numbers_ats() },
  ];

  const founders = [
    {
      name: m.about_founder_vaisakhan_name(),
      title: m.about_founder_vaisakhan_title(),
      bio: m.about_founder_vaisakhan_bio(),
      initial: "V",
      linkedIn: "https://www.linkedin.com/in/vaisakhan-arackal-sanu/",
    },
    {
      name: m.about_founder_chris_name(),
      title: m.about_founder_chris_title(),
      bio: m.about_founder_chris_bio(),
      initial: "C",
      linkedIn: "https://www.linkedin.com/in/chris-zanfir/",
    },
  ];

  return (
    <div className="flex flex-col" style={{ backgroundColor: "var(--bg)" }}>
      <JsonLd json={jsonLd} />

      {/* Hero */}
      <section className="pt-24 pb-16">
        <div className="mx-auto max-w-3xl px-6 text-center">
          <h1
            className="text-3xl font-bold tracking-tight sm:text-4xl"
            style={{
              fontFamily: "var(--font-display)",
              color: "var(--text)",
            }}
          >
            {m.about_title()}
          </h1>
          <p
            className="mt-4 text-base leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_subtitle()}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section
        className="py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_mission_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_mission_text()}
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_story_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_story_text()}
          </p>
        </div>
      </section>

      {/* Key Facts */}
      <section
        className="py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_facts_title()}
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
            {facts.map((fact) => (
              <div
                key={fact.label}
                className="rounded-[13px] p-4 text-center transition-all duration-250"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <div
                  className="mx-auto flex h-10 w-10 items-center justify-center rounded-[10px]"
                  style={{
                    background: "var(--accent-subtle)",
                    color: "var(--accent-text)",
                  }}
                >
                  {fact.icon}
                </div>
                <p
                  className="mt-3 text-xs font-medium"
                  style={{ color: "var(--text)" }}
                >
                  {fact.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Numbers */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold text-center"
            style={{ color: "var(--text)" }}
          >
            {m.about_numbers_title()}
          </h2>
          <div className="mt-8 grid grid-cols-2 gap-6 sm:grid-cols-4">
            {numbers.map((stat) => (
              <div
                key={stat.label}
                className="rounded-[13px] p-6 text-center transition-all duration-250"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <p
                  className="text-2xl font-bold"
                  style={{ color: "var(--accent)" }}
                >
                  {stat.value}
                </p>
                <p
                  className="mt-2 text-xs font-medium"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders */}
      <section
        className="py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_founders_title()}
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {founders.map((founder) => (
              <div
                key={founder.name}
                className="rounded-[13px] p-6 transition-all duration-250"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border-subtle)",
                }}
              >
                <div className="flex items-center gap-4">
                  <div
                    className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-full text-lg font-semibold"
                    style={{
                      background: "var(--accent-subtle)",
                      color: "var(--accent-text)",
                    }}
                  >
                    {founder.initial}
                  </div>
                  <div>
                    <p
                      className="text-sm font-semibold"
                      style={{ color: "var(--text)" }}
                    >
                      {founder.name}
                    </p>
                    <p
                      className="text-xs"
                      style={{ color: "var(--text-muted)" }}
                    >
                      {founder.title}
                    </p>
                  </div>
                </div>
                <p
                  className="mt-4 text-xs leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {founder.bio}
                </p>
                <a
                  href={founder.linkedIn}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium transition-colors duration-150 hover:opacity-80"
                  style={{ color: "var(--accent)" }}
                  aria-label={`${founder.name} on LinkedIn`}
                >
                  <svg
                    className="h-4 w-4"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  LinkedIn
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technology */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_tech_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_tech_text()}
          </p>
        </div>
      </section>

      {/* Company */}
      <section
        className="py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_company_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_company_text()}
          </p>
        </div>
      </section>

      {/* EU & GDPR */}
      <section className="py-16">
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_eu_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_eu_text()}
          </p>
        </div>
      </section>

      {/* EU AI Act Article 50 transparency features */}
      <section
        id="article-50"
        className="scroll-mt-24 py-16"
        style={{ backgroundColor: "var(--bg-secondary)" }}
        data-marketing-feature="article-50"
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_article50_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_article50_text()}
          </p>
          <Link
            to="/impressum"
            className="mt-4 inline-flex rounded-sm text-sm font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4"
            style={{ color: "var(--accent-text)" }}
          >
            {m.about_article50_link()}
          </Link>
        </div>
      </section>

      {/* BFSG-oriented accessibility features and evidence */}
      <section
        id="bfsg-accessibility"
        className="scroll-mt-24 py-16"
        data-marketing-feature="bfsg"
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            className="text-xl font-semibold"
            style={{ color: "var(--text)" }}
          >
            {m.about_bfsg_title()}
          </h2>
          <p
            className="mt-4 text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)" }}
          >
            {m.about_bfsg_text()}
          </p>
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3">
            <Link
              to="/barrierefreiheit"
              className="inline-flex rounded-sm text-sm font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{ color: "var(--accent-text)" }}
            >
              {m.about_bfsg_statement_link()}
            </Link>
            <Link
              to="/vpat"
              className="inline-flex rounded-sm text-sm font-semibold underline-offset-4 hover:underline focus-visible:outline-2 focus-visible:outline-offset-4"
              style={{ color: "var(--accent-text)" }}
            >
              {m.about_bfsg_vpat_link()}
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section
        className="py-24"
        style={{ backgroundColor: "var(--bg-secondary)" }}
      >
        <div className="mx-auto max-w-xl px-6 text-center">
          <Link to="/auth/signup" className="btn btn-accent btn-lg">
            {m.about_cta_text()}
          </Link>
        </div>
      </section>
    </div>
  );
}
